/**
 * Phase 8 T-22 §27.B — Resolve the intake config for the current route.
 *
 * Usage:
 *   ```tsx
 *   const intakeCfg = useAssistantIntakeConfig();
 *   if (intakeCfg && listIsEmpty) {
 *     return <AssistantIntake {...intakeCfg} firstName={firstName} onSubmit={…} />;
 *   }
 *   ```
 *
 * Returns `null` in two cases:
 *   1. The current route is on the §27.B **allowlist** (/home, /dashboard,
 *      /settings/*, /library, /clients) — these surfaces deliberately
 *      **never** render the intake.
 *   2. No config row in `INTAKE_CONFIGS` matches the current path.
 */

import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import {
  INTAKE_CONFIGS,
  INTAKE_DO_NOT_RENDER_ROUTES,
  type IntakeConfig,
} from '../intake.config';

/**
 * Checks whether `pathname` matches any of the allowlisted prefixes.
 * Exported for the unit test that iterates the config map.
 */
export function isIntakeAllowlistedRoute(pathname: string): boolean {
  const clean = pathname.replace(/\/+$/, '').toLowerCase();
  return INTAKE_DO_NOT_RENDER_ROUTES.some((prefix) => {
    const p = prefix.replace(/\/+$/, '').toLowerCase();
    return clean === p || clean.startsWith(`${p}/`);
  });
}

/**
 * Matches a config row against a concrete pathname. Supports `:param`
 * placeholders in the config route (e.g. `/bulk/:action`). Returns `true`
 * iff the number of segments matches and non-param segments are equal.
 */
export function matchesRoute(configRoute: string, pathname: string): boolean {
  const a = configRoute.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  const b = pathname.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const seg = a[i]!;
    if (seg.startsWith(':')) continue;
    if (seg.toLowerCase() !== b[i]!.toLowerCase()) return false;
  }
  return true;
}

/**
 * Public hook — returns the matched row or `null`.
 */
export function useAssistantIntakeConfig(): IntakeConfig | null {
  const { pathname } = useLocation();
  return useMemo(() => {
    if (isIntakeAllowlistedRoute(pathname)) return null;
    return INTAKE_CONFIGS.find((cfg) => matchesRoute(cfg.route, pathname)) ?? null;
  }, [pathname]);
}
